<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { VMaskInput } from 'vuetify/labs/VMaskInput'
import { useStorage } from '@vueuse/core'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: 'Wybierz godzinę',
  },
  minuteInterval: {
    type: [Number, String],
    default: 5,
  },
  required: {
    type: Boolean,
    default: false,
  },
  defaultHours: {
    type: Array,
    default: () => null,
  },
  hoursFrom: {
    type: String,
    default: null,
  },
  hoursTo: {
    type: String,
    default: null,
  },
  hoursOffset: {
    type: Number,
    default: 0,
  },
  overlay: {
    type: Boolean,
    default: true,
  },
  overlayZIndex: {
    type: Number,
    default: 9000,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: "default",
  },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const { t } = useI18n()

const timeMask = computed(() => {
  const beforeColon = String(props.modelValue || '').split(':')[0]

  return {
    mask: beforeColon.length <= 1 ? 'A:CD' : 'AB:CD',
    tokens: {
      A: {
        pattern: /[0-2]/,
        convert: v => v,
      },
      B: {
        pattern: /[0-9]/,
        convert: v => v,
      },
      C: {
        pattern: /[0-5]/,
        convert: v => v,
      },
      D: {
        pattern: /[0-9]/,
        convert: v => v,
      },
    },
  }
})

const menu = ref(false)

const tempHour = ref(null)
const tempMinute = ref(null)

const showSalonWorkingHours = useStorage('saloner-time-picker-show-salon-hours', true)

const hours = computed(() => {

  if(showSalonWorkingHours.value) {
    if (props.hoursFrom && props.hoursTo) {
      const fromHour = Number(props.hoursFrom.split(':')[0])
      const toHour = Number(props.hoursTo.split(':')[0])
      const start = Math.max(1, fromHour - props.hoursOffset)
      const end = Math.min(24, toHour + props.hoursOffset)

      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    }

    if (props.defaultHours && props.defaultHours.length > 0) {

      return props.defaultHours
    }
  }

  return Array.from({ length: 24 }, (_, i) => i )
})
function parseMinuteInterval(val) {
  if (typeof val === 'number') return val
  const parts = String(val).split(':')
  if (parts.length === 2 || parts.length === 3) {
    return Number(parts[0]) * 60 + Number(parts[1])
  }

  return Number(val) || 5
}

const minuteInterval = computed(() => parseMinuteInterval(props.minuteInterval))

const minutes = computed(() => {
  const count = Math.floor(60 / minuteInterval.value)

  return Array.from({ length: count }, (_, i) => i * minuteInterval.value)
})

const formatMinute = (m) => String(m).padStart(2, '0')
const formatHour = (h) => String(h).padStart(2, '0')

function getCurrentTimeRaw() {
  const now = new Date()

  return formatHour(now.getHours() || 24) + formatMinute(now.getMinutes())
}

function isValidComplete(value) {
  if (!value || typeof value !== 'string') return false
  const digits = value.replace(/\D/g, '')
  if (digits.length !== 4) return false
  const h = Number(digits.slice(0, 2))
  const m = Number(digits.slice(2))
  
  return h >= 1 && h <= 24 && m >= 0 && m <= 59
}

function ensureValue() {
  if (!props.required) return
  if (!isValidComplete(props.modelValue)) {
    emit('update:modelValue', getCurrentTimeRaw())
  }
}

const inputValue = computed({
  get: () => props.modelValue ?? '',
  set: (val) => {
    //emit('update:modelValue', val)
    const digits = (val || '').replace(/\D/g, '')
    if (digits.length === 4 && Number(digits) <= 2359) {
      emit('update:modelValue', digits.slice(0, 2) + ":" + digits.slice(2))

      return
    }

  },
})

onMounted(ensureValue)

function parseTimeString(value) {
  if (!value || typeof value !== 'string') return null

  let hour, minute

  const withColon = value.match(/^(\d{1,2}):(\d{2})$/)
  if (withColon) {
    hour = Number(withColon[1])
    minute = Number(withColon[2])
  } else {
    const raw = value.match(/^(\d{2})(\d{2})$/)
    if (!raw) return null
    hour = Number(raw[1])
    minute = Number(raw[2])
  }

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null
  if (hour < 1 || hour > 24) return null
  if (minute < 0 || minute > 59) return null

  return { hour, minute }
}

function syncTempFromModel() {
  const parsed = parseTimeString(props.modelValue)
  if (parsed) {
    tempHour.value = parsed.hour
    tempMinute.value = parsed.minute % minuteInterval.value === 0
      ? parsed.minute
      : null
  } else {
    tempHour.value = null
    tempMinute.value = null
  }
}

watch(menu, (opened) => {
  if (opened) syncTempFromModel()
})

function onBlur() {
  //const val = props.modelValue
  const val = inputValue.value
  const digits = (val || '').replace(/\D/g, '')

  if (digits.length === 0) {
    if (props.required) {
      emit('update:modelValue', getCurrentTimeRaw())
    }

    return
  }

  if (digits.length === 1) {
    emit('update:modelValue', getCurrentTimeRaw())

    return
  }

  if (digits.length === 2) {
    emit('update:modelValue', digits + '00')

    return
  }

  if (digits.length === 3) {
    emit('update:modelValue', digits + '0')
  }

  if (digits.length === 4 && Number(digits) <= 2359) {
    emit('update:modelValue', digits.slice(0, 2) + ":" + digits.slice(2))

    return
  }

}

function onCancel() {
  menu.value = false
  emit('cancel')
}

function onConfirm() {
  if (tempHour.value == null || tempMinute.value == null) return
  const raw = `${formatHour(tempHour.value)}:${formatMinute(tempMinute.value)}`
  emit('update:modelValue', raw)
  emit('confirm', raw)
  menu.value = false
}

const hasTimeChanged = computed(() => {
  if (tempHour.value == null || tempMinute.value == null) return false
  const newRaw = `${formatHour(tempHour.value)}:${formatMinute(tempMinute.value)}`
  const parsed = parseTimeString(props.modelValue)
  if (!parsed) return true
  const currentRaw = `${formatHour(parsed.hour)}:${formatMinute(parsed.minute)}`

  return newRaw !== currentRaw
})

function openMenu() {
  menu.value = true
}
</script>

<template>
  <div
    class="saloner-time-picker-wrapper d-flex align-stretch w-100"
    :class="{
      'disabled': props.disabled
    }"
  >
    <VMaskInput
      v-model="inputValue"
      :label="label"
      variant="outlined"
      :mask="timeMask"
      :disabled="props.disabled"
      class="saloner-time-picker-input extra-compact flex-grow-1"
      :class="{
        'xs-input' : props.size === 'x-small'
      }"
      density="compact"
      hide-details
      color="secondary"
      @blur="onBlur"
    />

    <Teleport to="body">
      <Transition name="overlay-fade">
        <div
          v-if="menu && overlay"
          class="time-picker-overlay"
          :style="`z-index: ${props.overlayZIndex}`"
          @click="onCancel"
        />
      </Transition>
    </Teleport>

    <VMenu
      v-model="menu"
      :disabled="props.disabled"
      :close-on-content-click="false"
      location="bottom end"
      class="saloner-time-picker-menu"
      :style="`z-index: ${props.overlayZIndex+1000}`"
      offset="8"
    >
      <template #activator="{ props: menuProps }">
        <div
          v-bind="menuProps"
          class="time-picker-btn align-center justify-center"
          :class="props.disabled ? 'd-none' : 'd-flex cursor-pointer'"
        >
          <VIcon icon="ri-time-line" />
        </div>
      </template>

      <VCard
        min-width="325"
        max-width="360"
        class="saloner-time-picker pa-4"
      >
        <div class="mb-2">
          <div class="d-flex align-center justify-space-between">
            <span class="text-subtitle-2">{{ $t('time_picker.hours') }}:</span>
            <span>
              <a
                v-if="(defaultHours && defaultHours.length > 0) || (hoursFrom && hoursTo)"
                class="text-caption"
                href="#"
                @click.prevent="showSalonWorkingHours = !showSalonWorkingHours"
              >
                <span class="mr-2">{{ showSalonWorkingHours ? $t('time_picker.show_all_hours') : $t('time_picker.show_less_hours') }}</span>
                <VIcon
                  class="ml-n2"
                  :icon="!showSalonWorkingHours ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                />
              </a>
            </span>
          </div>
        </div>

        <div class="grid hours mb-6">
          <VBtn
            v-for="h in hours"
            :key="h"
            size="small"
            variant="flat"
            :active="tempHour === h"
            :color="tempHour === h ? 'primary' : 'secondary'"
            class="cell"
            @click="tempHour = h"
          >
            {{ formatHour(h) }}
          </VBtn>
        </div>

        <div class="text-subtitle-2 mb-2">
          {{ $t('time_picker.minutes') }}:
        </div>

        <div class="grid minutes mb-6">
          <VBtn
            v-for="m in minutes"
            :key="m"
            size="small"
            variant="flat"
            :active="tempMinute === m"
            :color="tempMinute === m ? 'primary' : 'secondary'"
            class="cell"
            @click="tempMinute = m"
          >
            {{ formatMinute(m) }}
          </VBtn>
        </div>

        <div class="d-flex justify-space-between">
          <VBtn
            variant="outlined"
            color="secondary"
            @click="onCancel"
          >
            {{ $t('cancel') }}
          </VBtn>

          <VBtn
            variant="flat"
            color="green"
            :disabled="!hasTimeChanged"
            @click="onConfirm"
          >
            {{ $t('confirm') }}
          </VBtn>
        </div>
      </VCard>
    </VMenu>
  </div>
</template>

<style scoped>
.grid {
  display: grid;
  gap: 4px;
}

.grid.hours {
  grid-template-columns: repeat(6, 1fr);
}

.grid.minutes {
  grid-template-columns: repeat(6, 1fr);
}

.cell {
  width: 100%;
}

.saloner-time-picker-wrapper:not(.disabled) .saloner-time-picker-input :deep(.v-field) {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.saloner-time-picker-wrapper:not(.disabled) .saloner-time-picker-input :deep(.v-field__outline__end) {
  border-radius: 0 !important;
}

.small-input-long-label.saloner-time-picker-wrapper:not(.disabled) .saloner-time-picker-input :deep(.v-field__outline__end) {
  border-top-width: 0!important;
}

.saloner-time-picker-input :deep(.v-label.v-field-label--floating) {
  min-width: 170%!important;
  text-overflow: unset!important;
}

.time-picker-btn {
  width: 44px;
  border: 1px solid rgba(0,0,0,0.18);
  border-left: none; /* right border of the input field acts as the separator */
  border-top-right-radius: 6px; /* Default vuetify rounded corners */
  border-bottom-right-radius: 6px;
  transition: all 0.2s ease;
}

.time-picker-btn:hover {
  background-color: #E84A07 !important;
  color: white;
}
</style>

<style>
.time-picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.2s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.v-theme--light .time-picker-btn {
  background-color: #DFDFDF !important;
  border-color: rgba(0, 0, 0, 0.18) !important;
}

.v-theme--dark .time-picker-btn {
  background-color: #585F69 !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}
</style>
